import React from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Clock,
  Bookmark,
  Share2,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";

interface EditorialCardProps {
  title: string;
  description?: string;
  author?: string;
  authorTitle?: string;
  readTime?: string;
  category?: string;
  date?: string;
  href: string;
  imageAspect?: string;
  variant?: "default" | "compact" | "featured" | "minimal";
  bookmarkable?: boolean;
  shareable?: boolean;
  tags?: string[];
  className?: string;
}

export function EditorialCard({
  title,
  description,
  author,
  authorTitle,
  readTime,
  category,
  date,
  href,
  imageAspect = "aspect-[16/9]",
  variant = "default",
  bookmarkable = true,
  shareable = false,
  tags,
  className,
}: EditorialCardProps) {
  const isFeatured = variant === "featured";
  const isCompact = variant === "compact";
  const isMinimal = variant === "minimal";

  return (
    <article className={cn("group", className)}>
      <Link href={href} className="block">
        {!isMinimal && (
          <div
            className={cn(
              "rounded-xl overflow-hidden border border-border/50 mb-4 bg-neutral-50",
              isFeatured ? "aspect-[16/9]" : isCompact ? "aspect-[4/3]" : "aspect-[16/9]"
            )}
          >
            <div className="w-full h-full flex items-center justify-center text-muted-foreground/20">
              {isFeatured ? (
                <Bookmark className="h-12 w-12" />
              ) : (
                <ChevronRight className="h-8 w-8" />
              )}
            </div>
          </div>
        )}
        <div className="space-y-2.5">
          {!isMinimal && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {category && (
                <Badge variant="secondary" className="h-4 px-1.5 text-[0.625rem] font-medium rounded-full">
                  {category}
                </Badge>
              )}
              {readTime && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {readTime}
                </span>
              )}
            </div>
          )}
          <h3
            className={cn(
              "font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors duration-200",
              isFeatured ? "text-xl sm:text-2xl" : isCompact ? "text-sm" : "text-base",
              isMinimal ? "text-sm" : "line-clamp-2"
            )}
          >
            {title}
          </h3>
          {description && !isCompact && !isMinimal && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
              {description}
            </p>
          )}
          {!isMinimal && (author || date) && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
              {author && (
                <>
                  <span className="font-medium text-foreground">{author}</span>
                  {authorTitle && (
                    <span className="text-neutral-300">·</span>
                  )}
                  <span>{authorTitle}</span>
                </>
              )}
              {author && date && <span className="text-neutral-300">·</span>}
              {date && <span>{date}</span>}
            </div>
          )}
          {tags && tags.length > 0 && !isMinimal && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className="h-4 px-1.5 text-[0.625rem] capitalize rounded-full">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </Link>
      {(bookmarkable || shareable) && (
        <div className="mt-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {bookmarkable && (
            <Button variant="ghost" size="sm" asChild className="gap-1 p-0 h-auto text-xs text-muted-foreground hover:text-foreground">
              <Link href={href}>
                <Bookmark className="h-3 w-3" /> Save
              </Link>
            </Button>
          )}
          {shareable && (
            <Button variant="ghost" size="sm" asChild className="gap-1 p-0 h-auto text-xs text-muted-foreground hover:text-foreground">
              <Link href={href}>
                <Share2 className="h-3 w-3" /> Share
              </Link>
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
